/**
 * Segmenter calibration access (spec 022). The super-admin tunes the on-device
 * background segmenter (BiRefNet) + edge refine ONCE; the value is stored
 * platform-globally in the cloud and rides the existing settings warm-up cache, so
 * EVERY client (customer + studio) reads the same calibration here — no per-client
 * upload. Pure resolution + a model-apply side effect; the editor reads it in
 * autoRemoveBg, and `applySegModelFromCalibration` is called at warm-up so the right
 * ONNX is selected before the first auto-clean.
 */
import type { GuideKeyOptions } from "../editor/chromaKey.js";
import type { SegmentationCalibration } from "@genz/platform";
import { readCache } from "./cache.js";
import { CACHE_KEYS } from "./sessionWarmup.js";
import { setSegModel } from "./modelStore.js";

/** Engine defaults — an empty calibration resolves to exactly this. NB: `edge_feather_px` was
 *  deliberately changed 0 → 1 (soften the AI cutout edge by default), so this is no longer the
 *  original legacy behaviour; admins/per-template sliders can set 0 to restore the crisp cut. */
export const CALIBRATION_DEFAULTS: Required<SegmentationCalibration> = {
  model: "birefnet",
  mask_threshold: 0,
  mask_gain: 1.0,
  mask_gamma: 1.0,
  edge_choke_px: 2,
  // 1px feather softens the AI cutout edge by default (the "grainy edge" complaint);
  // admins can still set 0 globally (server clamp 0..10 permits it) and per-template
  // sliders override per template.
  edge_feather_px: 1,
  despill_amount: 1.0,
  preserve_luma: true,
  // The engine's shadowBoost / highlightBoost for a keyed magenta print area. They were fixed
  // engine constants; the owner asked to calibrate them once for every customer (2026-10-12).
  key_shadow_depth: 1.5,
  key_highlight_depth: 1.4,
  key_depth_all_templates: false,
  // Owner, 2026-10-15: flattening the Generate print guide loses the AI's details. Off: the
  // cloud keeps the AI's own magenta pixels; on: flat pure magenta, the camera lip kept.
  flatten_magenta: false,
  // The editor's Artwork shading and Highlights of a new template (owner, 2026-10-13).
  print_shading: 0.6,
  print_highlights: 0.4,
  // Half strength removes the block edges of an AI image and keeps the camera rim sharp.
  shading_smoothing: 0.5,
  // Off unless a super-admin raises it (owner, 2026-10-13: "I don't want it").
  case_highlights: 0,
  // The editor's Reference finish: Light gradient, Surface grain and Gloss sheen.
  print_gradient: 0.42,
  print_grain: 0.55,
  print_sheen: 1.2,
  // The design's ink range: full by default.
  print_black: 0,
  print_white: 1,
  light_revision: 0,
  // The print guide key as it shipped: tolerance 0.5, edge softness 1 px.
  key_tolerance: 0.5,
  key_softness: 1,
  // Owner, 2026-10-14: the effects the key keeps are black and white after keying.
  key_colour_removal: 1,
  key_by_purity: false,
  key_purity_start: 0.15,
  key_purity_full: 0.6,
  key_dark_keep: 0,
  // Owner, 2026-10-14: the bulk key was the one that came out right.
  key_method: "bulk",
  key_noise_floor: 4,
  key_snap_low: 0.04,
  key_snap_high: 0.96,
  key_flat_sample: 0.6,
};

/** The magenta key depth range, as a factor: 1 is the light exactly as the image recorded it. */
export const KEY_DEPTH_MIN = 1;
export const KEY_DEPTH_MAX = 3;

/** Read the global calibration from the warm-up settings cache, merged over defaults. */
export function resolveCalibration(): Required<SegmentationCalibration> {
  let raw: SegmentationCalibration | undefined;
  try {
    raw = readCache<{ segmenter_calibration?: SegmentationCalibration }>(CACHE_KEYS.settings)?.segmenter_calibration;
  } catch { /* no cache → defaults */ }
  const merged = { ...CALIBRATION_DEFAULTS, ...(raw ?? {}) };
  // Saved before the count existed, with the switch on: every template was at the platform light.
  if (raw?.light_revision === undefined && merged.key_depth_all_templates) merged.light_revision = 1;
  return merged;
}

/** A template's own light, as the editor saves it. `lightRev` is the platform count it was saved at. */
interface TemplateLight {
  strength?: number; highlights?: number; gradient?: number; grain?: number; sheen?: number;
  shadowBoost?: number; highlightBoost?: number; smoothing?: number; caseHighlights?: number;
  inkBlack?: number; inkWhite?: number;
  lightRev?: number;
}

/**
 * The light a template has now (owner, 2026-10-13). The values in Admin → AI matte are the
 * defaults, and a template may hold its own. "Apply these values to every template" raises the
 * platform count: a template saved at an older count takes the platform values until someone
 * saves it again, and from then on holds its own once more. Nothing is locked.
 */
export function templateLight<T extends TemplateLight>(shading: T | undefined): Omit<T, "lightRev"> | undefined {
  if (!shading) return shading;
  const { lightRev, ...own } = shading;
  const c = resolveCalibration();
  if ((lightRev ?? 0) >= c.light_revision) return own;
  return {
    ...own, strength: c.print_shading, highlights: c.print_highlights,
    gradient: c.print_gradient, grain: c.print_grain, sheen: c.print_sheen,
    shadowBoost: c.key_shadow_depth, highlightBoost: c.key_highlight_depth,
    inkBlack: c.print_black, inkWhite: c.print_white,
  };
}

/**
 * A saved template's shading as it RENDERS: `templateLight`, plus the smoothing and the case
 * highlights, which are always the platform's. Applied where a stored mock-up is read, so a
 * change in Admin → AI matte reaches the next render with no re-save and no re-download.
 */
export function withPlatformLight<T extends TemplateLight>(shading: T | undefined): Omit<T, "lightRev"> | undefined {
  const light = templateLight(shading);
  if (!light) return light;
  const c = resolveCalibration();
  return { ...light, smoothing: c.shading_smoothing, caseHighlights: c.case_highlights };
}

/** The key method a super-admin saved, if one was. */
function rawMethod(): GuideKeyOptions["method"] | undefined {
  try {
    return readCache<{ segmenter_calibration?: SegmentationCalibration }>(CACHE_KEYS.settings)?.segmenter_calibration?.key_method;
  } catch { return undefined; }
}

/** The print guide key's tolerance and edge softness, set once in Admin → AI matte. */
export function guideKeyOptions(): Required<GuideKeyOptions> {
  const c = resolveCalibration();
  return {
    // `key_by_purity` is how v0.2.129 saved the choice, before there were three.
    method: rawMethod() ?? (c.key_by_purity ? "purity" : c.key_method), tolerance: c.key_tolerance,
    noiseFloor: c.key_noise_floor, snapLow: c.key_snap_low, snapHigh: c.key_snap_high, flatSample: c.key_flat_sample,
    start: c.key_purity_start, full: c.key_purity_full, darkKeep: c.key_dark_keep,
    softness: c.key_softness, colour: c.key_colour_removal,
  };
}

/** Native segmenter post-processing params passed through removeBackgroundAI. */
export function nativeMaskParams(c: Required<SegmentationCalibration>): { maskThreshold: number; maskGain: number; maskGamma: number } {
  return { maskThreshold: c.mask_threshold, maskGain: c.mask_gain, maskGamma: c.mask_gamma };
}

/** Apply the calibration's model choice to the model store (call at warm-up / settings change). */
export function applySegModelFromCalibration(): void {
  setSegModel(resolveCalibration().model);
}
